/**
 * No Access Page
 * Displayed when a user has no tenant assignments
 * Allows users to submit access requests directly through the system
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Block as BlockIcon,
  HowToReg as RequestIcon,
  Logout as LogoutIcon,
  CheckCircle as SuccessIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import { usePermissions } from '../contexts/PermissionContext';
import { apiGet, apiPost, clearSelectedTenant } from '../utils/apiClient';
import logger from '../utils/logger';

function NoAccessPage() {
  const { user, logout } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null); // null, 'pending', 'rejected', 'submitted'
  const [existingRequest, setExistingRequest] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  // Check for existing access request and load available tenants
  const checkExistingRequest = useCallback(async () => {
    try {
      setLoading(true);

      // Clear any selected tenant to avoid 403 errors - user has no tenant access
      clearSelectedTenant();

      const requestResponse = await apiGet('/admin/access-requests/my-status').catch(err => {
        logger.warn('Could not fetch access request status', { message: err.message, status: err.response?.status });
        return { success: false, hasRequest: false };
      });

      logger.debug('Access request status checked', { event: 'access_request_status_check' });

      if (requestResponse.success && requestResponse.hasRequest) {
        logger.debug('Found existing access request', { event: 'existing_request_found', code: requestResponse.request?.status });
        setExistingRequest(requestResponse.request);
        setRequestStatus(requestResponse.request.status);
      }
    } catch (err) {
      logger.warn('Could not check existing request', { message: err.message, status: err.response?.status });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkExistingRequest();
  }, [checkExistingRequest]);

  const handleSubmitRequest = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await apiPost('/admin/access-requests', {
        reason_for_access: reason || null,
      });

      if (response.success) {
        setRequestStatus('submitted');
        setExistingRequest({
          submittedAt: response.request?.submittedAt || new Date().toISOString(),
        });
      }
    } catch (err) {
      if (err.message?.includes('request_exists')) {
        setError('You already have a pending request. Please wait for admin review.');
        checkExistingRequest();
      } else {
        setError(err.message || 'Failed to submit request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  // Render based on request status
  const renderContent = () => {
    // Request just submitted
    if (requestStatus === 'submitted') {
      return (
        <>
          <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Request Submitted
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your access request has been submitted successfully.
          </Typography>
          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              An administrator will review your request and grant access to the appropriate organization.
              You will be able to log in once approved.
            </Typography>
          </Alert>
        </>
      );
    }

    // Request pending
    if (requestStatus === 'pending') {
      return (
        <>
          <PendingIcon sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Request Pending
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your access request is being reviewed by an administrator.
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Submitted:</strong> {formatDate(existingRequest?.submittedAt)}
            </Typography>
            {existingRequest?.reason && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Reason:</strong> {existingRequest.reason}
              </Typography>
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please check back later or contact your administrator if you need urgent access.
          </Typography>
        </>
      );
    }

    // Request rejected
    if (requestStatus === 'rejected') {
      return (
        <>
          <RejectedIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Request Not Approved
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your access request was reviewed but could not be approved.
          </Typography>
          {existingRequest?.rejectionReason && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Reason:</strong> {existingRequest.rejectionReason}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            If you believe this is an error, please contact your system administrator directly.
          </Typography>
        </>
      );
    }

    // No request - show form
    return (
      <>
        <BlockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          No Organization Access
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your account <strong>{user?.email}</strong> is not currently assigned to any organization.
        </Typography>

        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="body2">
            Submit an access request below and an administrator will review it and assign you to the appropriate organization.
          </Typography>
        </Alert>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for access (optional)"
          placeholder="e.g., I need to manage contracts for African Alliance..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 2 }}
          disabled={submitting}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <RequestIcon />}
          onClick={handleSubmitRequest}
          disabled={submitting}
          fullWidth
          sx={{ mb: 2, backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
        >
          {submitting ? 'Submitting...' : 'Request Access'}
        </Button>
      </>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden',
        p: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Cpath d='M0 400 Q150 320 300 420 T600 360 T900 440 T1200 380' fill='none' stroke='rgba(148,163,184,0.18)' stroke-width='1.5'/%3E%3Cpath d='M0 430 Q180 350 360 460 T720 390 T1080 470 T1200 420' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 370 Q120 300 240 380 T480 340 T720 400 T960 350 T1200 370' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 460 Q200 390 400 490 T800 420 T1200 470' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 340 Q100 280 200 350 T400 310 T600 360 T800 320 T1000 370 T1200 340' fill='none' stroke='rgba(148,163,184,0.10)' stroke-width='1'/%3E%3Cpath d='M0 490 Q250 440 500 520 T1000 480 T1200 500' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 310 Q300 260 600 330 T1200 300' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cpath d='M0 520 Q200 480 400 550 T800 510 T1200 540' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cellipse cx='150' cy='200' rx='350' ry='250' fill='rgba(59,130,246,0.08)' /%3E%3Cellipse cx='950' cy='600' rx='400' ry='300' fill='rgba(99,102,241,0.07)' /%3E%3Cellipse cx='600' cy='80' rx='250' ry='180' fill='rgba(148,163,184,0.06)' /%3E%3C/svg%3E")
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pointerEvents: 'none',
        },
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 20px 60px rgba(0,0,0,0.3)', position: 'relative', zIndex: 1 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {renderContent()}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
            Logged in as {user?.email}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default NoAccessPage;
